import type { Page, ProductOperationVo, ProductPagingParams, ProductStatisticAllParams } from "@zhihaoscm/entity";
import { useNavigate } from "react-router";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps, type TableRequestData } from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";
import { formatNumber, toThousand } from "@/utils/number";
import { formatTimeEn } from "@/utils/formatter";

export interface UserGoodsStatisticsDialogProps {
    open: boolean;
    onClose: () => void;
    baseParams?: ProductStatisticAllParams;
    loadRows?: (params: ProductPagingParams & { outerKeyword?: string }) => Promise<Page<ProductOperationVo>>;
}

/** 用户商品统计明细默认接口，沿用旧 Vue custom-paging 契约。 */
export async function loadUserGoodsStatistics(params: ProductPagingParams & { outerKeyword?: string }) {
    return dashboardService.customPaging(params);
}

type TablePagingParams = Record<string, unknown>;

/** 将 UI 表格分页参数转换为旧接口要求的字符串 page/size。 */
export const normalizeUserGoodsStatisticsPagingParams = (
    params: TablePagingParams
): ProductPagingParams & { outerKeyword?: string } => ({
    ...(params as Omit<ProductPagingParams, "page" | "size"> & { outerKeyword?: string }),
    page: String(params.page ?? 1),
    size: String(params.size ?? 10)
});

/** 组合外层商品筛选条件和明细表分页条件，保持旧 Vue 请求字段命名。 */
export const buildUserGoodsStatisticsQuery = (
    rawParams: TablePagingParams,
    baseParams?: ProductStatisticAllParams
): ProductPagingParams & { outerKeyword?: string } => {
    const params = normalizeUserGoodsStatisticsPagingParams(rawParams);
    const { keyword, ...outerParams } = baseParams || {};
    return {
        ...outerParams,
        ...params,
        scope: outerParams.scope === 6 ? undefined : outerParams.scope,
        ...(keyword ? { outerKeyword: keyword } : {})
    };
};

const displayNumber = (value?: number) => (value === 0 ? 0 : formatNumber(value, 2));

/** 用户商品明细表列，保持 Vue 原有字段、排序字段和用户跳转入口。 */
const createColumns = (navigate: ReturnType<typeof useNavigate>): TableColumnProps<ProductOperationVo>[] => [
    {
        prop: "customerCode",
        label: "用户ID",
        minWidth: 140,
        search: {
            order: 1,
            el: "input",
            props: { placeholder: "请输入用户ID/手机号/个人实名/机构名称搜索", maxlength: 50 },
            key: "keyword"
        },
        render: ({ row }) => (
            <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => navigate(`/lianYunUser/userInfo?userId=${row.customerId ?? ""}`)}
            >
                {row.customerCode || "--"}
            </button>
        )
    },
    { prop: "name", label: "个人实名/机构名称", minWidth: 200 },
    { prop: "mobile", label: "注册手机号", minWidth: 135 },
    {
        prop: "totalSalesAmount",
        label: "总销售额(元)",
        sortable: "custom",
        render: ({ row }) => displayNumber(row.totalSalesAmount)
    },
    {
        prop: "settlementAmount",
        label: "结算金额(元)",
        sortable: "custom",
        render: ({ row }) => displayNumber(row.settlementAmount)
    },
    {
        prop: "refundAmount",
        label: "退款金额(元)",
        sortable: "custom",
        render: ({ row }) => displayNumber(row.refundAmount)
    },
    {
        prop: "totalSalesVolume",
        label: "总销量(吨)",
        sortable: "custom",
        render: ({ row }) => displayNumber(row.totalSalesVolume)
    },
    {
        prop: "actualSettledTons",
        label: "实际结算吨数(吨)",
        sortable: "custom",
        render: ({ row }) => displayNumber(row.actualSettledTons)
    },
    {
        prop: "productVisitCount",
        label: "商品访问次数(次)",
        sortable: "custom",
        render: ({ row }) => toThousand(row.productVisitCount)
    },
    {
        prop: "systemTotalStayTime",
        label: "系统停留时长",
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.systemTotalStayTime)
    },
    {
        prop: "totalStayTimeSec",
        label: "商品停留时长",
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.totalStayTimeSec)
    },
    {
        prop: "avgSingleStayTime",
        label: "平均单次停留时长",
        sortable: "custom",
        render: ({ row }) => formatTimeEn(row.avgSingleStayTime)
    }
];

/** 用户商品统计明细弹窗，复用 zhihao-ui-react Table/Dialog 并保留 Vue 的 1200 宽度。 */
export function UserGoodsStatisticsDialog({
    open,
    onClose,
    baseParams,
    loadRows = loadUserGoodsStatistics
}: UserGoodsStatisticsDialogProps) {
    const navigate = useNavigate();
    const columns = createColumns(navigate);

    const requestRows = async (rawParams: unknown): Promise<TableRequestData<ProductOperationVo>> => {
        const params = buildUserGoodsStatisticsQuery((rawParams || {}) as TablePagingParams, baseParams);
        const result = await loadRows(params);
        return {
            list: result.list || [],
            currentPage: Number(params.page),
            pageSize: Number(params.size),
            total: result.total || 0
        };
    };

    return (
        <ZhDialog open={open} title="用户商品统计明细" width={1200} onCancel={onClose} footer={null}>
            <ZhTable<ProductOperationVo>
                requestApi={requestRows}
                columns={columns}
                rowKey={row => String(row.customerId || row.customerCode || "user-goods-row")}
                initParam={{ page: 1, size: 10 }}
                emptyText="暂无数据"
            />
        </ZhDialog>
    );
}

export default UserGoodsStatisticsDialog;
